/**
 * Block geometry for plan objects: which block columns an area / building / path covers,
 * and the numbers that matter when building it (block counts, walls, floors, stacks).
 *
 * Conventions: every point is a block coordinate (x, z). A polygon runs through the
 * centres of its corner blocks, and its footprint = the blocks whose centre is inside
 * the polygon PLUS the blocks its edges run through — so the corner blocks you click
 * are always part of the shape (a 2-corner rectangle 0..4 is 5 × 5 blocks).
 */

export type Pt = [number, number];

/** Occupancy grid over a bounding box. cells[(z - z0) * w + (x - x0)] = 1 when covered. */
export interface BlockGrid {
  x0: number;
  z0: number;
  w: number;
  d: number;
  cells: Uint8Array;
  count: number;
}

/** Above this many cells in the bounding box we don't rasterise (counts become estimates). */
export const MAX_GRID_CELLS = 16_000_000;

export function bounds(pts: Pt[]) {
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (const [x, z] of pts) { x0 = Math.min(x0, x); x1 = Math.max(x1, x); z0 = Math.min(z0, z); z1 = Math.max(z1, z); }
  return { x0, x1, z0, z1, cx: (x0 + x1) / 2, cz: (z0 + z1) / 2 };
}

/** The four corners (clockwise seen from above) of the rectangle spanned by two corner blocks. */
export function rectCorners(a: Pt, b: Pt): Pt[] {
  const x0 = Math.min(a[0], b[0]), x1 = Math.max(a[0], b[0]);
  const z0 = Math.min(a[1], b[1]), z1 = Math.max(a[1], b[1]);
  return [[x0, z0], [x1, z0], [x1, z1], [x0, z1]];
}

/** 4 corners where every edge is parallel to the x or z axis (a plain rectangle). */
export function isAxisRect(pts: Pt[]) {
  if (pts.length !== 4) return false;
  for (let i = 0; i < 4; i++) {
    const a = pts[i], b = pts[(i + 1) % 4];
    if (a[0] !== b[0] && a[1] !== b[1]) return false;
  }
  const b = bounds(pts);
  const want = new Set(rectCorners([b.x0, b.z0], [b.x1, b.z1]).map((p) => p.join(",")));
  return pts.every((p) => want.has(p.join(","))) && new Set(pts.map((p) => p.join(","))).size === want.size;
}

/** Move corner i of an axis-aligned rectangle and keep it a rectangle (the opposite corner stays). */
export function dragRectCorner(pts: Pt[], i: number, to: Pt): Pt[] {
  const opp = pts[(i + 2) % 4];
  const n1 = (i + 1) % 4, n3 = (i + 3) % 4;
  const out = pts.map((p) => [...p] as Pt);
  out[i] = [to[0], to[1]];
  // n1 shared its z with corner i → it keeps sharing z with the moved corner
  const n1SharesZ = pts[n1][1] === pts[i][1] && pts[n1][0] !== pts[i][0];
  if (n1SharesZ) { out[n1] = [opp[0], to[1]]; out[n3] = [to[0], opp[1]]; }
  else { out[n1] = [to[0], opp[1]]; out[n3] = [opp[0], to[1]]; }
  return out;
}

/**
 * Blocks covered by a closed polygon (see conventions above). Degenerate inputs work too:
 * 1 point = that block, 2 points = the line between them.
 */
export function polygonGrid(pts: Pt[]): BlockGrid | null {
  if (!pts.length) return null;
  const b = bounds(pts);
  const x0 = Math.floor(b.x0), z0 = Math.floor(b.z0);
  const w = Math.floor(b.x1) - x0 + 1, d = Math.floor(b.z1) - z0 + 1;
  if (w * d > MAX_GRID_CELLS) return null;
  const cells = new Uint8Array(w * d);
  const n = pts.length;
  // interior: scanline through block centres (even–odd rule)
  if (n >= 3) {
    const xs: number[] = [];
    for (let z = z0; z < z0 + d; z++) {
      xs.length = 0;
      for (let i = 0, j = n - 1; i < n; j = i++) {
        const [xi, zi] = pts[i], [xj, zj] = pts[j];
        if ((zi > z) !== (zj > z)) xs.push(xi + ((z - zi) * (xj - xi)) / (zj - zi));
      }
      xs.sort((a, c) => a - c);
      const row = (z - z0) * w;
      for (let k = 0; k + 1 < xs.length; k += 2) {
        const a = Math.max(x0, Math.ceil(xs[k] - 1e-9)), c = Math.min(x0 + w - 1, Math.floor(xs[k + 1] + 1e-9));
        for (let x = a; x <= c; x++) cells[row + x - x0] = 1;
      }
    }
  }
  // outline: rasterise every edge (1 block per step along the longer axis) so edges are closed lines
  const edges = n >= 3 ? n : n - 1;
  for (let e = 0; e < Math.max(1, edges); e++) {
    const a = pts[e], c = pts[(e + 1) % n];
    const steps = Math.max(Math.abs(c[0] - a[0]), Math.abs(c[1] - a[1]), 0);
    for (let s = 0; s <= steps; s++) {
      const t = steps ? s / steps : 0;
      const x = Math.round(a[0] + (c[0] - a[0]) * t), z = Math.round(a[1] + (c[1] - a[1]) * t);
      cells[(z - z0) * w + (x - x0)] = 1;
    }
  }
  let count = 0;
  for (let i = 0; i < cells.length; i++) count += cells[i];
  return { x0, z0, w, d, cells, count };
}

/** Block columns within width/2 of the polyline through block centres (what you'd pave). */
export function pathGrid(pts: Pt[], width: number): BlockGrid | null {
  if (!pts.length) return null;
  const r = Math.max(0.5, width / 2);
  const b = bounds(pts);
  const pad = Math.ceil(r) + 1;
  const x0 = Math.floor(b.x0) - pad, z0 = Math.floor(b.z0) - pad;
  const w = Math.floor(b.x1) + pad - x0 + 1, d = Math.floor(b.z1) + pad - z0 + 1;
  if (w * d > MAX_GRID_CELLS) return null;
  const cells = new Uint8Array(w * d);
  const c = pts.length === 1 ? [pts[0], pts[0]] : pts;
  for (let i = 0; i < c.length - 1; i++) {
    const [ax, az] = c[i], [bx, bz] = c[i + 1];
    const gx0 = Math.floor(Math.min(ax, bx) - r), gx1 = Math.ceil(Math.max(ax, bx) + r);
    const gz0 = Math.floor(Math.min(az, bz) - r), gz1 = Math.ceil(Math.max(az, bz) + r);
    const dx = bx - ax, dz = bz - az, l2 = dx * dx + dz * dz || 1;
    for (let z = gz0; z <= gz1; z++)
      for (let x = gx0; x <= gx1; x++) {
        const raw = ((x - ax) * dx + (z - az) * dz) / l2;
        // flat ends at the first/last clicked block (round joints in between, so bends have no gaps)
        if (pts.length > 1 && ((i === 0 && raw < -1e-6) || (i === c.length - 2 && raw > 1 + 1e-6))) continue;
        const t = Math.max(0, Math.min(1, raw));
        const qx = ax + t * dx - x, qz = az + t * dz - z;
        if (qx * qx + qz * qz <= r * r + 0.01) cells[(z - z0) * w + (x - x0)] = 1;
      }
  }
  let count = 0;
  for (let i = 0; i < cells.length; i++) count += cells[i];
  return { x0, z0, w, d, cells, count };
}

export function gridHas(g: BlockGrid, x: number, z: number) {
  const lx = x - g.x0, lz = z - g.z0;
  return lx >= 0 && lz >= 0 && lx < g.w && lz < g.d && g.cells[lz * g.w + lx] === 1;
}

/** All covered block columns as [x, z]. */
export function gridBlocks(g: BlockGrid | null, limit = Infinity): Pt[] {
  const out: Pt[] = [];
  if (!g) return out;
  for (let z = 0; z < g.d; z++)
    for (let x = 0; x < g.w; x++)
      if (g.cells[z * g.w + x]) { out.push([g.x0 + x, g.z0 + z]); if (out.length >= limit) return out; }
  return out;
}

/**
 * Wall ring: covered blocks that touch the outside (8-neighbourhood), i.e. the closed
 * one-block-thick wall you'd build around the footprint.
 */
export function ringCount(g: BlockGrid): number {
  let n = 0;
  for (let z = 0; z < g.d; z++)
    for (let x = 0; x < g.w; x++) {
      if (!g.cells[z * g.w + x]) continue;
      let edge = false;
      for (let dz = -1; dz <= 1 && !edge; dz++)
        for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx, nz = z + dz;
          if (nx < 0 || nz < 0 || nx >= g.w || nz >= g.d || !g.cells[nz * g.w + nx]) { edge = true; break; }
        }
      if (edge) n++;
    }
  return n;
}

/**
 * Outline of the covered blocks along block edges (for drawing walls), as merged straight
 * segments [x0, z0, x1, z1] in world block-edge coordinates.
 */
export function gridOutline(g: BlockGrid): [number, number, number, number][] {
  const out: [number, number, number, number][] = [];
  const has = (x: number, z: number) => x >= 0 && z >= 0 && x < g.w && z < g.d && g.cells[z * g.w + x] === 1;
  // edges parallel to x: between row z-1 and z
  for (let z = 0; z <= g.d; z++) {
    for (const side of [0, 1]) {
      let start = -1;
      for (let x = 0; x <= g.w; x++) {
        const on = x < g.w && (side === 0 ? has(x, z) && !has(x, z - 1) : has(x, z - 1) && !has(x, z));
        if (on && start < 0) start = x;
        if (!on && start >= 0) { out.push([g.x0 + start, g.z0 + z, g.x0 + x, g.z0 + z]); start = -1; }
      }
    }
  }
  // edges parallel to z: between column x-1 and x
  for (let x = 0; x <= g.w; x++) {
    for (const side of [0, 1]) {
      let start = -1;
      for (let z = 0; z <= g.d; z++) {
        const on = z < g.d && (side === 0 ? has(x, z) && !has(x - 1, z) : has(x - 1, z) && !has(x, z));
        if (on && start < 0) start = z;
        if (!on && start >= 0) { out.push([g.x0 + x, g.z0 + start, g.x0 + x, g.z0 + z]); start = -1; }
      }
    }
  }
  return out;
}

/** "870 blocks ≈ 13 stacks + 38" helper → { stacks: 13, rest: 38 } */
export function stacks(n: number, size = 64) {
  return { stacks: Math.floor(n / size), rest: n % size };
}

export function formatStacks(n: number, size = 64): string {
  if (n < size) return `${n}`;
  const s = stacks(n, size);
  return `${s.stacks} stack${s.stacks === 1 ? "" : "s"}${s.rest ? ` + ${s.rest}` : ""}`;
}
